import type { ReactNode } from "react";

import { Checkbox } from "@/components/ui/checkbox";

interface SettingsSectionCardProps {
  title: string;
  children: ReactNode;
}

/** docs/ui/component-inventory.md's `SettingsSectionCard` — a titled
 * container grouping related `SettingsRow`s (Account/Notifications/
 * Security/Privacy). */
export function SettingsSectionCard({ title, children }: SettingsSectionCardProps) {
  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <h2 className="mb-4 text-h4 text-foreground">{title}</h2>
      <div className="divide-y divide-border">{children}</div>
    </div>
  );
}

interface ToggleRowProps {
  label: string;
  description?: string;
  value: boolean;
  onChange: (value: boolean) => void;
  isDisabled?: boolean;
}

/** docs/ui/component-inventory.md's `SettingsRow`, toggle variant. Reuses
 * the existing `Checkbox` primitive rather than introducing a new `Switch`
 * component for one page — the same call `FilterSidebar` and the Sell
 * wizard's "Negotiable" field already made for boolean toggles. */
export function ToggleSettingsRow({ label, description, value, onChange, isDisabled }: ToggleRowProps) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
      <div>
        <p className="text-body text-foreground">{label}</p>
        {description && <p className="text-body-sm text-muted-foreground">{description}</p>}
      </div>
      <Checkbox
        checked={value}
        onCheckedChange={(checked) => onChange(checked === true)}
        disabled={isDisabled}
        aria-label={label}
      />
    </div>
  );
}

interface DestructiveSettingsRowProps {
  label: string;
  description?: string;
  actionLabel: string;
  onClick: () => void;
}

/** `SettingsRow`, destructive-link variant — e.g. "Delete Account." Never
 * mutates on its own click; the caller wires `onClick` to open a
 * `ConfirmDialog`, per that component's own note on not shipping
 * destructive one-click buttons. */
export function DestructiveSettingsRow({
  label,
  description,
  actionLabel,
  onClick,
}: DestructiveSettingsRowProps) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
      <div>
        <p className="text-body text-foreground">{label}</p>
        {description && <p className="text-body-sm text-muted-foreground">{description}</p>}
      </div>
      <button
        type="button"
        onClick={onClick}
        className="shrink-0 text-body-sm font-medium text-destructive hover:underline"
      >
        {actionLabel}
      </button>
    </div>
  );
}
